// 後端 v2.4 一次新增的業務錯誤碼集中對照（附錄 A）。
// 全域錯誤攔截器（composables/useApiFetch.ts handleApiError）與 chat 串流流程
// （stores/agentChat.ts）都查這張表，避免這些 code fall through 成「未知錯誤」。
//
// 只放「需要統一文案/行為」的業務碼；認證類數字碼仍由 constants/errorCodes.ts 管理。

export interface ApiErrorCodeMeta {
  /** 對應的 i18n 訊息 key（toast 文案） */
  i18nKey: string;
  /**
   * 後端標記是否可重試。false 代表前端不要自動重送（例如額度不足，避免一直撞上游計費）。
   * 預設視為不可自動重試。
   */
  retryable?: boolean;
  /**
   * 額度類錯誤專用：後端有帶 used/limit 這組 safe detail 時改用這個 key，
   * 文案可帶 {used}/{limit}/{tier}。缺欄位時 fall back 回 i18nKey，
   * 避免 toast 出現沒被取代的 {used} 佔位字。
   */
  quotaDetailI18nKey?: string;
}

export const API_ERROR_CODE_MAP: Record<string, ApiErrorCodeMeta> = {
  // run 超過 timeout_seconds。常見於：run 等審批太久，核准時早已超時。
  AGENT_TIMEOUT: { i18nKey: 'errorMessages.api.agentTimeout', retryable: true },
  // run 用完 max_rounds 仍未完成（通常是工具一直失敗，把回合數耗光）。重試多半也會再撞一次。
  AGENT_ROUND_LIMIT: { i18nKey: 'errorMessages.api.agentRoundLimit', retryable: false },
  // 503 — runtime 發版維護中（平台小助手 / agent chat）。維護結束前重送也只會再撞一次。
  AGENT_RUNTIME_MAINTENANCE: { i18nKey: 'errorMessages.api.agentRuntimeMaintenance', retryable: false },
  // 402 — 餘額/額度不足。文案刻意通用，絕不可顯示上游帶回的 wallet 金額或帳務細節。
  INSUFFICIENT_CREDITS: { i18nKey: 'errorMessages.api.insufficientCredits', retryable: false },
  // 422 — 輸入含機密（API key/密碼等）。引導使用者改用憑證設定，原始 secret 不顯示、不保存、不重送。
  SECRET_INTAKE_REQUIRED: { i18nKey: 'errorMessages.api.secretIntakeRequired', retryable: false },
  // 422 — KB workspace-bindings 4 路由
  INVALID_KNOWLEDGE_WORKSPACE_BINDING_REQUEST: {
    i18nKey: 'errorMessages.api.invalidKnowledgeWorkspaceBinding',
  },
  // 422 — identity binding POST
  INVALID_IDENTITY_BINDING_REQUEST: { i18nKey: 'errorMessages.api.invalidIdentityBindingRequest' },
  // 409 — identity binding 重複（呼叫端會用 existing_binding_id 定位既有設定，這裡是 fallback 文案）
  IDENTITY_BINDING_CONFLICT: { i18nKey: 'errorMessages.api.identityBindingConflict' },
  // 422 — audit facets
  INVALID_AUDIT_FACETS_REQUEST: { i18nKey: 'errorMessages.api.invalidAuditFacets' },
  // 422 — DSR 反查
  INVALID_DSR_SEARCH_REQUEST: { i18nKey: 'errorMessages.api.invalidDsrSearch' },
  // 409 — discard 非草稿（呼叫端有更精準提示，這裡是 fallback 文案）
  RELEASE_NOT_DRAFT: { i18nKey: 'errorMessages.api.releaseNotDraft' },
  // 422 — discard body 不合法
  INVALID_RELEASE_DISCARD_REQUEST: { i18nKey: 'errorMessages.api.invalidReleaseDiscard' },
  // 409 — rollback 只接受目前 active 的 release；要回舊版應 promote 該版
  ROLLBACK_SOURCE_NOT_ACTIVE: { i18nKey: 'errorMessages.api.rollbackSourceNotActive' },
  // 409 — rolled_back / archived 的 release 不可再 promote / rollback
  RELEASE_NOT_PROMOTABLE: { i18nKey: 'errorMessages.api.releaseNotPromotable' },
  // 400 — benchmark run body/async 不合法
  INVALID_BENCH_RUN_REQUEST: { i18nKey: 'errorMessages.api.invalidBenchRun' },
  // 503 — broker session：此 identity binding 尚無憑證材料（未完成 OAuth 授權，也無 fallback 憑證）
  BROKER_SESSION_UNAVAILABLE: {
    i18nKey: 'errorMessages.api.brokerSessionUnavailable',
    retryable: false,
  },
  // 4xx — OAuth 連結：provider 不支援（current HEAD 僅 google_drive）
  OAUTH_PROVIDER_UNSUPPORTED: {
    i18nKey: 'errorMessages.api.oauthProviderUnsupported',
    retryable: false,
  },
  // 4xx — soul / profile / version 軟歸檔（DELETE，僅 inactive；active 另回 409）
  INVALID_SOUL_DELETE_REQUEST: { i18nKey: 'errorMessages.api.invalidSoulDelete' },
  // 4xx — directive 提案生命週期（PATCH / reject / discard，僅 proposed 狀態）
  INVALID_DIRECTIVE_PATCH_REQUEST: { i18nKey: 'errorMessages.api.invalidDirectivePatch' },
  INVALID_DIRECTIVE_REJECT_REQUEST: { i18nKey: 'errorMessages.api.invalidDirectiveReject' },
  INVALID_DIRECTIVE_DISCARD_REQUEST: { i18nKey: 'errorMessages.api.invalidDirectiveDiscard' },
  // 403 — HIVE chat sandbox 執行：該組織未開通 sandbox policy（取代舊 INTERNAL_PREVIEW_REQUIRED）
  HIVE_SANDBOX_NOT_ENABLED: {
    i18nKey: 'errorMessages.api.hiveSandboxNotEnabled',
    retryable: false,
  },
  // 422 — 方案降級被拒（僅允許 upgrade）
  PLAN_CAN_NOT_DOWNGRADE: { i18nKey: 'errorMessages.api.planCanNotDowngrade', retryable: false },
  // 422 — 已有訂閱時重複 buy plan
  SUBSCRIPTION_ALREADY_EXISTS: {
    i18nKey: 'errorMessages.api.subscriptionAlreadyExists',
    retryable: false,
  },
  // 400 — delegation：target agent 不存在於 target workspace
  INVALID_TARGET_AGENT: { i18nKey: 'errorMessages.api.invalidTargetAgent', retryable: false },
  // 404 — delegation：target agent 不存在
  DELEGATION_TARGET_AGENT_NOT_FOUND: {
    i18nKey: 'errorMessages.api.delegationTargetAgentNotFound',
    retryable: false,
  },
  // 409 — delegation：重複 binding
  DELEGATION_ALREADY_EXISTS: {
    i18nKey: 'errorMessages.api.delegationAlreadyExists',
    retryable: false,
  },
  // 4xx — 操作類工具風控審批中
  SESSION_PENDING_APPROVAL: {
    i18nKey: 'errorMessages.api.sessionPendingApproval',
    retryable: false,
  },
  // 422 — /system/bot 空 message 或舊 messages[] 格式
  messages_empty: { i18nKey: 'errorMessages.api.messagesEmpty', retryable: false },
  // 429 — slot 月度 MXC 使用上限已達（次月自動重置；調高上限或關閉限額可立即解封）。
  // 對話入口（/v1/api/chat 等）屬 embed 端，這裡是 console 側共用的 fallback 文案。
  SLOT_USAGE_LIMIT_EXCEEDED: {
    i18nKey: 'errorMessages.api.gatewayUsageLimitExceeded',
    retryable: false,
  },
  // 400 — PUT /v2/app/ai/slot/usage-limit 帶負數 monthly_mxc_limit
  err_invalid_monthly_mxc_limit: {
    i18nKey: 'errorMessages.api.invalidMonthlyMxcLimit',
    retryable: false,
  },
  // 400 — PUT /v2/app/ai/slot/usage-limit 帶超出 0-100 的 alert_threshold
  err_invalid_alert_threshold: {
    i18nKey: 'errorMessages.api.invalidAlertThreshold',
    retryable: false,
  },

  // === v2.10 套餐額度（Cron / Cloud Run deployment slot）===
  // 409 — tenant active cron 數已達 scheduled_task_limit。引導升級或先停用/刪除排程，不可自動重送。
  CRON_QUOTA_EXCEEDED: {
    i18nKey: 'errorMessages.api.cronQuotaExceeded',
    quotaDetailI18nKey: 'errorMessages.api.cronQuotaExceededDetail',
    retryable: false,
  },
  // 409 — 部署 slot 佔用已達方案上限（佔用含 reserved / 狀態待確認，不只 active）
  DEPLOYMENT_SLOT_QUOTA_EXCEEDED: {
    i18nKey: 'errorMessages.api.deploymentQuotaExceeded',
    quotaDetailI18nKey: 'errorMessages.api.deploymentQuotaExceededDetail',
    retryable: false,
  },
  // 503 — 取不到 plan snapshot（Payment 暫時不可用）。★ 不是額度為 0，文案必須是「稍後重試」。
  PLAN_QUOTA_UNAVAILABLE: { i18nKey: 'errorMessages.api.planQuotaUnavailable', retryable: true },

  // === v2.10 deployment slot 生命週期 ===
  // 410 — slot 已釋放：視為成功，刷新列表與用量即可（呼叫端已特別處理，這裡是 fallback 文案）
  DEPLOYMENT_SLOT_ALREADY_RELEASED: {
    i18nKey: 'errorMessages.api.deploymentSlotAlreadyReleased',
    retryable: false,
  },
  // 404 — 不存在或無權；不可據此推測跨 scope 是否存在
  DEPLOYMENT_SLOT_NOT_FOUND: {
    i18nKey: 'errorMessages.api.deploymentSlotNotFound',
    retryable: false,
  },
  // 409 — 目前狀態不允許此操作
  DEPLOYMENT_SLOT_STATE_INVALID: {
    i18nKey: 'errorMessages.api.deploymentSlotStateInvalid',
    retryable: false,
  },
  // 409 — 對帳中：等 reconcile，不要重複建立或重複釋放
  DEPLOYMENT_SLOT_STATE_RECONCILING: {
    i18nKey: 'errorMessages.api.deploymentSlotReconciling',
    retryable: false,
  },
  // 404 — operation 不存在：停止輪詢，改用列表重新同步
  DEPLOYMENT_OPERATION_NOT_FOUND: {
    i18nKey: 'errorMessages.api.deploymentOperationNotFound',
    retryable: false,
  },
  // 422 — list/detail query 不合法
  INVALID_DEPLOYMENT_SLOT_FILTER: {
    i18nKey: 'errorMessages.api.invalidDeploymentSlotFilter',
    retryable: false,
  },
  // 400/422 — DELETE 缺 reason 或長度不在 1-200
  INVALID_DEPLOYMENT_UNDEPLOY_REQUEST: {
    i18nKey: 'errorMessages.api.invalidDeploymentUndeployRequest',
    retryable: false,
  },

  // === v2.10 directive 軟歸檔（committed directive）===
  // 409 — immutable directive 需 operator break-glass 流程，一般 admin 不能歸檔
  DIRECTIVE_IMMUTABLE_ARCHIVE_REQUIRES_BREAK_GLASS: {
    i18nKey: 'errorMessages.api.directiveArchiveRequiresBreakGlass',
    retryable: false,
  },
  // 409 — 指定的 replacement directive 尚未 committed
  DIRECTIVE_REPLACEMENT_NOT_COMMITTED: {
    i18nKey: 'errorMessages.api.directiveReplacementNotCommitted',
    retryable: false,
  },
  // 409 — replacement 成環
  DIRECTIVE_REPLACEMENT_CYCLE: {
    i18nKey: 'errorMessages.api.directiveReplacementCycle',
    retryable: false,
  },
  // 409 — replacement 鏈太深
  DIRECTIVE_REPLACEMENT_CHAIN_TOO_DEEP: {
    i18nKey: 'errorMessages.api.directiveReplacementChainTooDeep',
    retryable: false,
  },
  // 409 — 該 directive 當前狀態不可歸檔（例如仍是 proposed，應走 reject/discard）
  DIRECTIVE_NOT_ARCHIVABLE: {
    i18nKey: 'errorMessages.api.directiveNotArchivable',
    retryable: false,
  },
  // 400/422 — archive body 不合法（reason 缺漏或長度不在 1-500）
  INVALID_DIRECTIVE_ARCHIVE_REQUEST: {
    i18nKey: 'errorMessages.api.invalidDirectiveArchive',
    retryable: false,
  },

  // === v2.10 Quick Create 取消 ===
  // 409 — run 已是終態（重複取消同一 run 是冪等 200，這裡是其它終態）
  QUICK_CREATE_RUN_CLOSED: {
    i18nKey: 'errorMessages.api.quickCreateRunClosed',
    retryable: false,
  },
  // 409 — mutation 被 fence 擋下（有更新的操作接手）
  QUICK_CREATE_MUTATION_FENCED: {
    i18nKey: 'errorMessages.api.quickCreateMutationFenced',
    retryable: false,
  },
  // 404 — run 不存在或無權
  QUICK_CREATE_RUN_NOT_FOUND: {
    i18nKey: 'errorMessages.api.quickCreateRunNotFound',
    retryable: false,
  },

  // === Central Skill Governance（BFF PR #803/804/805）===
  // 409 — 樂觀鎖過期（update / disable / uninstall 三支都會撞）。
  // ★ 不可自動重送：必須先 GET 拿最新 revision，讓使用者看過現況再決定要不要重做。
  CENTRAL_SKILL_REVISION_CONFLICT: {
    i18nKey: 'errorMessages.api.centralSkillRevisionConflict',
    retryable: false,
  },
  // 409 — 同一把 idempotency_key 送了不同 payload。
  // 解法是「使用者改了內容就換新 key」，不是重送。
  CENTRAL_SKILL_IDEMPOTENCY_CONFLICT: {
    i18nKey: 'errorMessages.api.centralSkillIdempotencyConflict',
    retryable: false,
  },
  // 409 — 租戶 rollout 未開啟／目前狀態不允許此操作。屬於狀態問題，重送不會變好。
  CENTRAL_SKILL_ROLLOUT_DISABLED: {
    i18nKey: 'errorMessages.api.centralSkillRolloutDisabled',
    retryable: false,
  },
  // 429 — 租戶層級的建立速率限制，帶 Retry-After（秒）。
  // 呼叫端要 disabled + 倒數，重試時**沿用原本的 idempotency_key**。
  CENTRAL_SKILL_CREATE_RATE_LIMITED: {
    i18nKey: 'errorMessages.api.centralSkillCreateRateLimited',
    retryable: true,
  },
  // 403 — 身分未經驗證（BFF 未注入可信 principal）。純後端／設定問題，UI 不提供任何「升權」入口。
  UNVERIFIED_IDENTITY_CONTEXT: {
    i18nKey: 'errorMessages.api.unverifiedIdentityContext',
    retryable: false,
  },
  // 403 — 該操作限組織管理員。同樣不提供升權入口，只說明權限不足。
  INTERNAL_ADMIN_REQUIRED: {
    i18nKey: 'errorMessages.api.internalAdminRequired',
    retryable: false,
  },
};

/**
 * Central Skill 的 422 是一整個家族（`CENTRAL_SKILL_<something>_INVALID`），
 * 值域沒有列舉、後端也還會再加。逐個列進上表只會漏，改用前綴+後綴比對，
 * 讓未知的新成員也能落到同一句通用 validation 文案，而不是 fall through 成「未知錯誤」。
 */
const CENTRAL_SKILL_INVALID_PATTERN = /^CENTRAL_SKILL_[A-Z0-9_]*INVALID$/;

export const isCentralSkillValidationCode = (code?: string | null): boolean =>
  !!code && CENTRAL_SKILL_INVALID_PATTERN.test(code);

/** quota 類錯誤（v2.10）後端允許帶的 safe detail；除此之外不會有金額/餘額等帳務資訊 */
export interface QuotaErrorDetail {
  /** 超出的維度：deployment / scheduled_task */
  dimension?: string;
  used: number;
  limit: number;
  /** 當前方案名稱，例如 Scale */
  tier?: string;
}

/**
 * 從錯誤 details 取出 quota safe detail；used/limit 任一缺漏或不是數字就回 null
 * （寧可顯示通用文案，也不要 toast 出「已用 undefined/undefined」）。
 */
export const parseQuotaErrorDetail = (details?: unknown): QuotaErrorDetail | null => {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  const used = Number(d.used);
  const limit = Number(d.limit);
  if (!Number.isFinite(used) || !Number.isFinite(limit)) return null;
  return {
    dimension: typeof d.dimension === 'string' ? d.dimension : undefined,
    used,
    limit,
    tier: typeof d.tier === 'string' ? d.tier : undefined,
  };
};

// 刻意不收 CRON_TASK_EXPIRED（410）：它代表 task 已過期且額度已釋放，是「已完成」不是錯誤，
// 由 stores/agentAutomation.ts 當成功處理並刷新列表；放進這張表會讓全域攔截器誤跳錯誤 toast。

/**
 * 解析錯誤碼要顯示的 i18n key 與參數。額度類錯誤在後端有帶 used/limit 時
 * 會改用帶數字的文案（例如「排程已用 15/15」）。未知碼回 null。
 */
export const resolveApiErrorMessage = (
  code?: string | null,
  details?: unknown
): { i18nKey: string; params?: Record<string, unknown> } | null => {
  const meta = code ? API_ERROR_CODE_MAP[code] : undefined;
  if (!meta) {
    // 家族式錯誤碼（目前只有 Central Skill 的 422）走前綴比對
    if (isCentralSkillValidationCode(code)) {
      return { i18nKey: 'errorMessages.api.centralSkillInvalidRequest' };
    }
    return null;
  }
  if (meta.quotaDetailI18nKey) {
    const quota = parseQuotaErrorDetail(details);
    if (quota) {
      return {
        i18nKey: meta.quotaDetailI18nKey,
        params: { used: quota.used, limit: quota.limit, tier: quota.tier ?? '' },
      };
    }
  }
  return { i18nKey: meta.i18nKey };
};

/** 是否為已知的業務錯誤碼（含家族式比對） */
export const isKnownApiErrorCode = (code?: string | null): boolean =>
  (!!code && Object.prototype.hasOwnProperty.call(API_ERROR_CODE_MAP, code)) ||
  isCentralSkillValidationCode(code);

/**
 * 取得錯誤碼對應的 i18n key；未知碼回 null（讓呼叫端 fallback 到原本的處理）。
 */
export const apiErrorCodeI18nKey = (code?: string | null): string | null =>
  (code && API_ERROR_CODE_MAP[code]?.i18nKey) ||
  (isCentralSkillValidationCode(code) ? 'errorMessages.api.centralSkillInvalidRequest' : null);
